<template>
  <!-- 第一个弹窗：选择猎头公司 -->
  <DialogLayout v-if="!showConfirmDialog" :class-name="'confirm-dialog'" @close="handleClose" :style="{
    width: width + 'px'
  }">
    <template #title>{{ t('job.dialog.openToAgent.title') }}</template>
    <template #default>
      <div class="open-title">{{ t('job.dialog.openToAgent.description', { jobId: props.jobId || '' }) }}</div>
      <div class="open-form">
        <div class="label">{{ t('job.dialog.openToAgent.label') }}</div>
        <div class="form-select">
          <el-select v-model="agents" multiple>
            <el-option
              v-for="agent in agentList"
              :key="agent.value"
              :label="agent.label"
              :value="agent.value"
            />
          </el-select>
        </div>
      </div>
    </template>
    <template #footer>
      <el-button
        type="default"
        class="confirm-button confirm-button-default"
        @click="handleCancel"
      >
        {{ t('common.cancel') }}
      </el-button>
      <el-button
        class="confirm-button"
        type="primary"
        @click="handleConfirm"
        :loading="confirmLoading"
      >
        {{ t('common.save') }}
      </el-button>
    </template>
  </DialogLayout>

  <!-- 第二个弹窗：确认关闭开放 -->
  <DialogLayout v-else :class-name="'confirm-dialog'" @close="handleClose" :style="{
    width: '560px'
  }">
    <template #title>{{ t('job.dialog.openToAgent.title') }}</template>
    <template #default>
      <div class="confirm-content">
        <div class="confirm-text">
          <LocaleSentence :text="t('job.dialog.openToAgent.confirmRemoveContent')">
            <template #count>
              <span class="highlight">{{ removedAgentsWithStats.length }}</span>
            </template>
            <template #totalCount>
              <span>{{ jobId }}</span>
            </template>
          </LocaleSentence>
        </div>
        <AgentInterviewStatTable 
          :agentStats="removedAgentsWithStats" 
          @resume-click="resumeClick"
          @interview-click="interviewClick"
        />
      </div>
    </template>
    <template #footer>
      <el-button
        type="default"
        class="confirm-button confirm-button-default"
        @click="handleConfirmCancel"
      >
        {{ t('common.cancel') }}
      </el-button>
      <el-button
        class="confirm-button confirm-button-danger"
        type="primary"
        @click="handleConfirmConfirm"
        :loading="confirmLoading"
      >
        {{ t('common.confirm') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, h } from 'vue';
import type { ConfirmModalProps } from './confirm-types';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import LocaleSentence from '@/components/basic-components/locale-sentence/index.vue';
import AgentInterviewStatTable from './components/AgentInterviewStatTable.vue';
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import {
  getHeadhuntingCompanySelectors,
  jobHeadhuntingOpen
} from '@/api/headhunting';
import { getHeadhuntingOpenInterviewStat } from '@/api/job';
const { t } = useI18n()
interface OpenToAgentProps extends ConfirmModalProps {
  jobId?: string | number;
  width?: number;
  agents?: Array<{ name: string; id: number }>;
  onInterviewClick: Function;
  onResumeClick: Function;
}

const props = withDefaults(
  defineProps<OpenToAgentProps>(),
  {
    title: '提示',
    showClose: true,
    width: 560,
  }
);

const emit = defineEmits<{
  cancel: [data?: any];
  confirm: [data?: any];
  close: [data?: any];
}>();

const agents = ref<number[]>([])
const originalAgents = ref<number[]>([])
const agentList = ref<Array<{ label: string; value: number; name: string; id: number }>>([])
const showConfirmDialog = ref(false)
const removedAgentsWithStats = ref<Array<{
  headhuntingId: number;
  headhuntingName: string;
  resumeScreeningCount: number;
  interviewCount: number;
}>>([])
const confirmLoading = ref(false)

const totalRemovedCount = computed(() => {
  return removedAgentsWithStats.value.reduce((sum, item) =>
    sum + item.resumeScreeningCount + item.interviewCount, 0
  );
});

const handleCancel = async () => {
  let result: any = undefined;
  if (props.cancelButton !== false && props.cancelButton?.onClick) {
    result = await props.cancelButton.onClick();
  }
  emit('cancel', result);
};

const handleConfirm = async () => {
  if (!props.jobId) {
    ElMessage.warning(t('job.dialog.openToAgent.selectJobRequired'));
    return;
  }

  confirmLoading.value = true;
  try {
    // 找出被删除的猎头公司
    const removedAgentIds = originalAgents.value.filter(id => !agents.value.includes(id));

    if (removedAgentIds.length > 0) {
      // 调用API检查被删除的猎头公司是否有未进入内定的选考
      const statData = await getHeadhuntingOpenInterviewStat({
        jobId: Number(props.jobId),
        headhuntingCompanyIds: agents.value.length ? agents.value : [],
      });

      // 筛选出有未进入内定选考的猎头公司
      const agentsWithStats = statData.filter(item =>
        item.resumeScreeningCount > 0 || item.interviewCount > 0
      );

      if (agentsWithStats.length > 0) {
        // 展示第二个确认弹窗
        removedAgentsWithStats.value = agentsWithStats;
        showConfirmDialog.value = true;
        confirmLoading.value = false;
        return;
      }
    }

    // 没有需要确认的，直接保存
    await jobHeadhuntingOpen({
      jobIds: [Number(props.jobId)],
      headhuntingCompanyIds: agents.value
    });
    ElMessage.success(t('job.dialog.openToAgent.success'));
    emit('confirm', agents.value);
  } catch (error) {
    console.error('检查猎头开放失败:', error);
    ElMessage.error(t('job.dialog.openToAgent.failed'));
  } finally {
    confirmLoading.value = false;
  }
};

// 第二个弹窗的取消按钮：恢复被删除的猎头公司
const handleConfirmCancel = () => {
  // 把被删除的猎头公司重新加回去
 agents.value = [...new Set([...originalAgents.value])];

  // 关闭第二个弹窗，回到第一个弹窗
  showConfirmDialog.value = false;
  removedAgentsWithStats.value = [];
};

// 第二个弹窗的确认按钮：保存并终止选考
const handleConfirmConfirm = async () => {
  if (!props.jobId) {
    return;
  }

  confirmLoading.value = true;
  try {
    await jobHeadhuntingOpen({
      jobIds: [Number(props.jobId)],
      headhuntingCompanyIds: agents.value
    });
    ElMessage.success(t('job.dialog.openToAgent.success'));
    emit('confirm', agents.value);
  } catch (error) {
    console.error('对猎头开放失败:', error);
    ElMessage.error(t('job.dialog.openToAgent.failed'));
  } finally {
    confirmLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
const resumeClick = (item: any) => {
  props.onResumeClick?.(item)
}
const interviewClick = (item: any) => {
  props.onInterviewClick?.(item)
}
onMounted(() => {
  getHeadhuntingCompanySelectors().then(res => {
    if (props.agents && props.agents.length > 0) {
      const initialAgentIds = props.agents.map(agent => agent.id);
      agents.value = [...initialAgentIds];
      originalAgents.value = [...initialAgentIds];
    }
    agentList.value = res.map(item => {
      return {
        label: item.name,
        value: item.id,
        name: item.name,
        id: item.id,
      }
    });
  })
})
</script>

<style lang="scss" scoped>
.confirm-dialog {
  min-width: 509px;
  background: #ffffff;
  overflow: hidden;
}

.open-title {
  font-size: 14px;
  line-height: 22px;
  color: #495057;
  word-break: break-word;
  white-space: pre-line;
}
.open-form {
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  .label {
    font-size: 14px;
    font-weight: 500;
    line-height: 35px;
    color: #495057;
    margin-right: 20px;
  }
  .form-select {
    flex: 1;
    :deep(.el-select__wrapper) {
      padding: 9px 12px;
    }
    :deep(.el-select__selected-item .el-tag) {
      background: #F4EFFD;
      border-radius: 4px;
    }
  }
}

.confirm-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-text {
  font-size: 14px;
  line-height: 22px;
  color: #495057;
}

.highlight {
  color: var(--el-color-primary);
  font-weight: 500;
}

.confirm-button {
  min-width: 72px;
  height: 35px;
  padding: 4px 16px;
  font-size: 14px;
  line-height: 22px;
  cursor: pointer;
  transition: all 0.3s;
  outline: none;

  &:active {
    transform: translateY(1px);
  }
}
.confirm-button-default {
  background: #ffffff;
  color: #495057;
  font-weight: normal;
  cursor: pointer;
  &:hover {
    color: var(--el-color-primary);
  }
}

.confirm-button-danger {
  background: var(--el-color-danger);
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: var(--el-color-danger);
  }
}
</style>
